// convert-corpus.ts — the "Convert AISDLC-2.0 Corpus" toggle's DECISION
// SURFACE (#4249, P5 of the plan for #4194). Pure functions only:
// create-dialog.ts owns the DOM, this module owns every judgment the panel
// makes — the create-release.ts doctrine, so the suite drills the exact
// logic the mounted dialog runs.
//
// The owner's ask: "When creating a new release and selecting a branch, we
// should have an optional toggle to 'Convert AISDLC-2.0 Corpus'. It should
// show the list of epics that exist in `thoughts/shared/epics/` and allow
// the user to check or uncheck inclusion."
//
// ── THE LOAD-BEARING PROPERTY: THE OFF PATH IS TODAY'S PATH ───────────────
// This feature is ADDITIVE. With the toggle OFF — its default — the create
// flow must be what it was before this file existed, not "working". That is
// why `convertRequestBody` returns an object with NO KEYS when the toggle is
// off, rather than `{ convert_corpus: null }` or `{ convert_corpus: false }`:
// a key whose value means "no" still changes the POST body, and the POST
// body is the only artefact the server sees. The suite pins it by driving
// the PINNED PRE-#4249 dialog and this one over identical input and
// comparing the two bodies, with a planted byte proving the comparator can
// say DIFFERS.
//
// ── THE SECOND PROPERTY: FOUR ANSWERS, NOT ONE EMPTY LIST ─────────────────
// P4 (corpus/epic-discovery.ts) went to some trouble so that a provider MISS,
// an ABSENT `thoughts/shared/epics/`, and a REAL EMPTY directory are three
// distinguishable values. Rendering all three as "no epics" would undo that
// in the one place a user can see it, so `convertPanelVm` keeps them apart
// as 'miss' / 'absent' / 'empty' and says something different about each.
//
// ── THE THIRD: UNSUPPORTED IS NOT UNCHECKED ───────────────────────────────
// An epic the converter's status-map seat does not teach (P2's
// `--list-taught-epics`) CANNOT be converted: P1 made the converter refuse an
// unmapped status before writing a byte. So such a row renders as
// UNSUPPORTED — never as an unchecked checkbox the user can tick, which
// would offer a choice that can only fail. A taught-list MISS makes every
// row unsupported too, with the miss named: not knowing which epics are
// taught is not the same as knowing they all are, and the safe reading of
// "unknown" is the one that cannot promise a conversion.

/** The toggle's visible name — the owner's words, verbatim.
 *
 * It read 'Convert AISDLC-2.0 Corpus' under #4249, which were also the
 * owner's words at the time. The owner renamed it on 2026-09-18: "a checkbox
 * on Create VM 'upgrade corpus'". The name is the user's whole model of what
 * the control does, so it tracks the latest wording and the suite pins the
 * LITERAL — an arm that compares the rendered label against this constant
 * cannot refuse a rename, because both sides move together. */
export const CONVERT_TOGGLE_LABEL = 'Upgrade corpus';

/** The one-line explanation beside it. Names where the epics come from, so
 * an empty panel is readable without opening a log. */
export const CONVERT_TOGGLE_HINT =
  'Optional: convert the 2.0 epics under thoughts/shared/epics/ on the cut base into this release’s corpus.';

// ── THE FOURTH: THE CHECKBOX IS GATED ON THE BRANCH, NOT ON THE TOGGLE ────
// #4246, the owner 2026-09-18: "a checkbox on Create VM 'upgrade corpus',
// DISABLED unless the selected branch has a corpus at `thoughts/shared/` in
// the repo". Under #4249 the control was always enabled and the read happened
// only AFTER it was ticked, so the only way to learn that a branch has nothing
// to upgrade was to ask for an upgrade of it. The gate therefore runs on
// BRANCH SELECTION and is INDEPENDENT of `enabled` — it has to be, because it
// is the input that decides whether `enabled` may become true at all. That is
// why `checkboxEnabled` / `checkboxReason` are computed BEFORE the OFF early
// return below and ride every vm this module returns, the OFF one included.
//
// It changes NOTHING about the POST body: the gate is an input to the DOM,
// not to `convertRequestBody`, and the OFF path still returns an object with
// NO KEYS (the load-bearing property at the top of this file).

/** What the branch-selection probe has learned about `thoughts/shared/` on
 * the cut base. Fed on BRANCH SELECTION, never on toggle-on.
 *
 * 'miss' and 'absent' are kept apart for the same reason the panel states
 * are: "we could not ask" is not "there is nothing there", and a checkbox
 * disabled for the first reason is a transient the user can retry, while the
 * second is a property of the branch they picked. */
export type ConvertProbe = 'idle' | 'loading' | 'miss' | 'absent' | 'present';

/** The corpus root the owner named. The probe reads the epic directory under
 * it (`/api/vcs/convert-epics`), whose `present: true` is the strongest
 * available evidence that this root exists on the ref — there is no cheaper
 * read, and a second endpoint for the parent dir would be a second way to be
 * wrong. The user-facing sentences name the ROOT, because that is the thing
 * the owner's sentence is about. */
export const UPGRADE_PROBE_PATH = 'thoughts/shared/';

/** The whole gate, pure: `present` enables, everything else disables WITH A
 * REASON. A disabled control with no reason is the failure this phase exists
 * to fix one level down — the user could not see why. */
export function checkboxGate(
  ref: string, probe: ConvertProbe,
): { checkboxEnabled: boolean; checkboxReason: string | null } {
  const at = ref || '(no branch selected)';
  switch (probe) {
    case 'present':
      return { checkboxEnabled: true, checkboxReason: null };
    case 'loading':
      return { checkboxEnabled: false, checkboxReason: `Reading ${UPGRADE_PROBE_PATH} on ${at}…` };
    case 'miss':
      return {
        checkboxEnabled: false,
        checkboxReason:
          `Could not read ${UPGRADE_PROBE_PATH} on ${at} — this is NOT “this branch has no corpus”.`,
      };
    case 'absent':
      return {
        checkboxEnabled: false,
        checkboxReason: `No ${UPGRADE_PROBE_PATH} on ${at} — nothing to upgrade`,
      };
    case 'idle':
    default:
      return {
        checkboxEnabled: false,
        checkboxReason: ref
          ? `${UPGRADE_PROBE_PATH} on ${at} has not been read yet.`
          : 'Select a source branch first — the corpus is read on that branch.',
      };
  }
}

/** Has the probe ANSWERED that this branch does not qualify?
 *
 * NOT the same question as `!checkboxEnabled`, and the difference is the
 * whole of the forced-OFF rule below. 'idle' and 'loading' disable the
 * control because there is no positive evidence YET — but they are
 * transients, and a branch edit passes through 'loading' on every keystroke,
 * so un-ticking an already-ON box there would make the box impossible to keep
 * ticked while typing. 'miss' is an UNKNOWN, not a no: the submit gate
 * already refuses it BY NAME and says so, which is more use to the user than
 * a silently cleared checkbox. Only 'absent' is the owner's sentence —
 * the branch was read and it HAS no corpus. */
export function probeDisqualifies(probe: ConvertProbe): boolean {
  return probe === 'absent';
}

/** The fetch lane, as a probe. ONE mapping, so the dialog never re-derives
 * it and the suite drives the same function the dialog runs. A `done` payload
 * whose discovery did not answer is a MISS, not an absence — the distinction
 * the panel states already carry, kept here too. */
export function probeFromFetch(f: ConvertFetch): ConvertProbe {
  if (f.state === 'idle') return 'idle';
  if (f.state === 'loading') return 'loading';
  if (f.state === 'error') return 'miss';
  const d = f.payload.discovery;
  if (!d.ok) return 'miss';
  if (d.present === false) return 'absent';
  return 'present';
}

/** The sentence an epic row carries when the taught list could not be read.
 * It is a PREFIX: the miss's own detail follows. */
export const TAUGHT_UNKNOWN_PREFIX =
  'the converter’s taught-epic list could not be read, so no epic can be offered';

/** What the discovery half looks like once it is over the wire. Structurally
 * the server's `EpicDiscovery`; restated here because the client tree does
 * not import server types. */
export interface EpicDiscoveryDto {
  ok: boolean;
  present?: boolean;
  epics?: string[];
  non_dir_entries?: string[];
  reason?: string;
  detail?: string;
  measured?: string;
  provider?: string;
  forge_kind?: string | null;
  ref?: string;
  path?: string;
}

/** The taught half. `ok: false` is a MISS; it is never an empty list. */
export interface TaughtEpicsDto {
  ok: boolean;
  epics?: string[];
  seat_dir?: string | null;
  converter?: string | null;
  reason?: string;
  detail?: string;
  measured?: string;
}

export interface ConvertEpicsDto {
  ref: string;
  path: string;
  discovery: EpicDiscoveryDto;
  taught: TaughtEpicsDto;
}

/** The fetch lane, as data. 'idle' = the toggle is off or nothing asked yet. */
export type ConvertFetch =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'done'; payload: ConvertEpicsDto }
  | { state: 'error'; detail: string };

export interface ConvertPanelInput {
  /** The toggle. FALSE is the default and the identity path. */
  enabled: boolean;
  /** The cut base the list was (or will be) read at. */
  ref: string;
  fetch: ConvertFetch;
  /** Epics the user explicitly UNCHECKED. Supported epics are included by
   * default — the owner's flow is "see all the epics, uncheck the ones you
   * do not want", so the default has to be the inclusive one. */
  unchecked?: readonly string[];
  /** The branch-selection probe. Defaults to 'idle', which DISABLES the
   * checkbox — the safe reading of "nobody has asked yet" is the one that
   * cannot offer an upgrade of a branch that may have nothing to upgrade. */
  probe?: ConvertProbe;
}

export type EpicRowState = 'selectable' | 'unsupported';

export interface EpicRowVm {
  epic: string;
  state: EpicRowState;
  /** Selectable rows only; an unsupported row is never checked. */
  checked: boolean;
  /** UNSUPPORTED rows are disabled — the control cannot be ticked at all. */
  disabled: boolean;
  /** Why this row is unsupported (null on a selectable row). */
  reason: string | null;
}

export type ConvertPanelState =
  /** Toggle off: no panel, no list, no key on the POST body. */
  | 'off'
  /** Toggle on, the read has not answered yet. */
  | 'loading'
  /** The read did not happen or the forge refused — NOT "no epics". */
  | 'miss'
  /** The forge answered DEFINITIVELY that the directory is not at this ref. */
  | 'absent'
  /** The directory exists at this ref and holds no epic directories. */
  | 'empty'
  /** Epics to choose from. */
  | 'list';

export interface ConvertPanelVm {
  state: ConvertPanelState;
  ref: string;
  /** The panel's own sentence — different for every state above. */
  message: string | null;
  rows: EpicRowVm[];
  /** The epics that would ride the POST, in discovery order. */
  selected: string[];
  counts: { total: number; supported: number; unsupported: number; selected: number };
  /** Does the create button stay reachable? ON with nothing convertible is
   * a request the console cannot honour, so it blocks BY NAME rather than
   * silently creating a release with no conversion. OFF never blocks. */
  submitOk: boolean;
  submitReason: string | null;
  /** One line naming what was measured — never a bare OK. */
  measured: string;
  /** May the checkbox be ticked at all? Computed from `probe` and NOT from
   * `enabled`, so it is meaningful on the OFF vm — which is the only vm that
   * exists while the user is deciding whether to tick it. */
  checkboxEnabled: boolean;
  /** Why not (null when enabled). The dialog renders it as the control's
   * `title`; the suite reads it as text. */
  checkboxReason: string | null;
}

type Gate = { checkboxEnabled: boolean; checkboxReason: string | null };

const OFF_VM = (ref: string, gate: Gate): ConvertPanelVm => ({
  ...gate,
  state: 'off',
  ref,
  message: null,
  rows: [],
  selected: [],
  counts: { total: 0, supported: 0, unsupported: 0, selected: 0 },
  submitOk: true,
  submitReason: null,
  measured: 'convert-panel: toggle OFF — no epic read, no list, and no key added to the create body',
});

function taughtReason(t: TaughtEpicsDto): string {
  const why = [t.reason, t.detail].filter(Boolean).join(': ');
  return `${TAUGHT_UNKNOWN_PREFIX} (${why || 'no reason reported'})`;
}

/** The whole judgment, pure and synchronous. */
export function convertPanelVm(input: ConvertPanelInput): ConvertPanelVm {
  const ref = input.ref;
  // BEFORE the OFF return, deliberately: the gate is what decides whether the
  // toggle may be turned on, so it cannot be a property of it being on.
  const gate = checkboxGate(ref, input.probe ?? 'idle');
  if (!input.enabled) return OFF_VM(ref, gate);

  const f = input.fetch;
  if (f.state === 'idle' || f.state === 'loading') {
    return {
      ...OFF_VM(ref, gate),
      state: 'loading',
      message: `Reading thoughts/shared/epics/ on ${ref}…`,
      submitOk: false,
      submitReason: 'the epic list for this branch has not answered yet',
      measured: `convert-panel: toggle ON, ref ${ref} — read in flight`,
    };
  }
  if (f.state === 'error') {
    return {
      ...OFF_VM(ref, gate),
      state: 'miss',
      message: `Could not read the epic list for ${ref}: ${f.detail}`,
      submitOk: false,
      submitReason: `the epic list for ${ref} could not be read: ${f.detail}`,
      measured: `convert-panel: toggle ON, ref ${ref} — MISS (request): ${f.detail}`,
    };
  }

  const { discovery: d, taught: t, path: p } = f.payload;
  if (!d.ok) {
    const why = [d.reason, d.detail].filter(Boolean).join(': ') || 'no reason reported';
    return {
      ...OFF_VM(ref, gate),
      state: 'miss',
      message: `Could not list ${p} on ${ref} — ${why}. This is NOT “this branch has no epics”.`,
      submitOk: false,
      submitReason: `the epic list for ${ref} could not be read: ${why}`,
      measured: `convert-panel: toggle ON, ref ${ref} — MISS (${d.reason ?? 'unclassified'}): ${d.detail ?? ''}`,
    };
  }
  if (d.present === false) {
    return {
      ...OFF_VM(ref, gate),
      state: 'absent',
      message: `No ${p}/ on ${ref} — there is no 2.0 corpus on this branch to convert.`,
      submitOk: false,
      submitReason: `${ref} has no ${p}/ — turn the toggle off, or pick a branch that has one`,
      measured: `convert-panel: toggle ON, ref ${ref} — ${p}/ ABSENT at this ref (answered, not a miss)`,
    };
  }

  const names = Array.isArray(d.epics) ? d.epics : [];
  if (names.length === 0) {
    return {
      ...OFF_VM(ref, gate),
      state: 'empty',
      message: `${p}/ exists on ${ref} but holds no epic directories.`,
      submitOk: false,
      submitReason: `${p}/ on ${ref} holds no epics — turn the toggle off, or pick another branch`,
      measured: `convert-panel: toggle ON, ref ${ref} — ${p}/ present and EMPTY (answered, not a miss)`,
    };
  }

  const taughtOk = t.ok === true && Array.isArray(t.epics);
  const taughtSet = new Set(taughtOk ? (t.epics as string[]) : []);
  const unchecked = new Set(input.unchecked ?? []);
  const rows: EpicRowVm[] = names.map((epic) => {
    if (!taughtOk) {
      return { epic, state: 'unsupported' as const, checked: false, disabled: true, reason: taughtReason(t) };
    }
    if (!taughtSet.has(epic)) {
      return {
        epic,
        state: 'unsupported' as const,
        checked: false,
        disabled: true,
        reason:
          `the converter’s status-map seat does not teach '${epic}' ` +
          `(it teaches ${taughtSet.size}: ${[...taughtSet].join(', ') || 'none'}) — ` +
          'converting it would REFUSE on an unmapped status',
      };
    }
    return {
      epic,
      state: 'selectable' as const,
      checked: !unchecked.has(epic),
      disabled: false,
      reason: null,
    };
  });

  const supported = rows.filter((r) => r.state === 'selectable');
  const selected = rows.filter((r) => r.checked).map((r) => r.epic);
  const counts = {
    total: rows.length,
    supported: supported.length,
    unsupported: rows.length - supported.length,
    selected: selected.length,
  };
  const submitOk = selected.length > 0;
  return {
    ...gate,
    state: 'list',
    ref,
    message:
      `${counts.total} epic(s) on ${ref}: ${counts.supported} convertible, ` +
      `${counts.unsupported} unsupported.`,
    rows,
    selected,
    counts,
    submitOk,
    submitReason: submitOk
      ? null
      : counts.supported === 0
        ? `none of the ${counts.total} epic(s) on ${ref} is supported by the converter — turn the toggle off`
        : 'no epic is selected — tick at least one, or turn the toggle off',
    measured:
      `convert-panel: toggle ON, ref ${ref} — ${counts.total} epic(s), ${counts.supported} supported, ` +
      `${counts.unsupported} unsupported, ${counts.selected} selected [${selected.join(', ')}]`,
  };
}

/**
 * What this panel CONTRIBUTES to the create POST body.
 *
 * THE OFF PATH RETURNS AN OBJECT WITH NO KEYS. Spreading it leaves the body
 * byte-identical to the pre-#4249 body — which is the one assertion this
 * phase is judged on. A `convert_corpus: null` here would pass every
 * behavioural test and still change what the server receives.
 */
export function convertRequestBody(vm: ConvertPanelVm): Record<string, unknown> {
  if (vm.state !== 'list' || vm.selected.length === 0) return {};
  return { convert_corpus: { ref: vm.ref, epics: vm.selected } };
}
